import { environment } from '@environments/environment';

/**
 * Available Sierra domains for cross-domain navigation
 * Add more domains here as needed (e.g., 'order', 'finance')
 */
export type SierraDomain = 'inventory';

/**
 * Configuration for cross-domain navigation
 */
export interface CrossDomainNavigationOptions {
  /** The target Sierra domain */
  domain: SierraDomain;
  /** The path to navigate to (without leading slash) */
  path: string;
  /** Query parameters to include in the URL */
  queryParams?: Record<string, string | number | boolean | null | undefined>;
  /** Whether to include the JWT token automatically (default: true) */
  includeToken?: boolean;
  /** The window target (default: '_blank') */
  target?: string;
}

/**
 * Gets the base URL for a Sierra domain from environment configuration
 */
function getDomainBaseUrl(domain: SierraDomain): string {
  const urlMap: Record<SierraDomain, string> = {
    inventory: environment.domainUrls.inventoryUrl,
  };

  const baseUrl = urlMap[domain];
  if (!baseUrl) {
    throw new Error(`Domain URL not configured for: ${domain}`);
  }

  return baseUrl;
}

/**
 * Builds a cross-domain URL with optional token and query parameters
 */
export function buildCrossDomainUrl(
  domain: SierraDomain,
  path: string,
  queryParams?: Record<string, string | number | boolean | null | undefined>,
  token?: string | null,
): string {
  const baseUrl = getDomainBaseUrl(domain);
  const finalBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  const url = new URL(finalBase + path);

  // Add token if provided
  if (token) {
    url.searchParams.set('token', token);
  }

  // Add additional query params
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Navigates to a cross-domain URL in a new window/tab
 * This function requires the JWT token to be passed from the calling component
 */
export function navigateCrossDomain(options: CrossDomainNavigationOptions, token?: string | null): void {
  const { domain, path, queryParams, includeToken = true, target = '_blank' } = options;

  const finalToken = includeToken ? token : undefined;
  const url = buildCrossDomainUrl(domain, path, queryParams, finalToken);

  window.open(url, target);
}
